import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface Props {
  text: ReactNode;
  color?: string;
  width?: number;
  placement?: 'top' | 'bottom';
}

/**
 * Icoană (i) cu popover tooltip pe hover/focus. Popover-ul e portalat în
 * document.body cu position: fixed — altfel ar fi clipuit de orice ancestor
 * cu overflow:auto|hidden (ex. body-ul modalului de vault).
 */
export default function InfoTooltip({
  text,
  color,
  width = 260,
  placement = 'bottom',
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const id = useId();
  const iconColor = color ?? 'var(--text-muted)';

  const computePos = () => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const centerX = r.left + r.width / 2;
    const top =
      placement === 'bottom' ? r.bottom + 6 : r.top - 6; // "top": bottom edge of tooltip 6px above icon
    setPos({ top, left: centerX });
  };

  useLayoutEffect(() => {
    if (!open) return;
    computePos();
    const onScrollOrResize = () => computePos();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, placement]);

  useEffect(() => {
    if (!open) setPos(null);
  }, [open]);

  const popStyle: CSSProperties | null = pos
    ? {
        position: 'fixed',
        zIndex: 1000,
        width,
        left: pos.left,
        top: placement === 'bottom' ? pos.top : undefined,
        bottom:
          placement === 'top'
            ? Math.max(0, window.innerHeight - pos.top)
            : undefined,
        transform: 'translateX(-50%)',
        background: 'var(--bg-elevated)',
        color: 'var(--text)',
        border: '1px solid var(--border)',
        boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
        borderRadius: 6,
        padding: '8px 10px',
        fontSize: 11,
        lineHeight: 1.45,
        fontWeight: 400,
        textAlign: 'left',
        whiteSpace: 'normal',
        pointerEvents: 'none',
      }
    : null;

  return (
    <span
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        ref={anchorRef}
        type="button"
        aria-label={t('shell_ui.info_tooltip.label')}
        aria-describedby={open ? id : undefined}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center rounded-full cursor-help"
        style={{
          width: 14,
          height: 14,
          border: `1px solid ${iconColor}`,
          color: iconColor,
          fontSize: 10,
          fontFamily: 'serif',
          fontStyle: 'italic',
          lineHeight: 1,
          background: 'transparent',
        }}
      >
        i
      </button>
      {open && popStyle &&
        createPortal(
          <span id={id} role="tooltip" style={popStyle}>
            {text}
          </span>,
          document.body,
        )}
    </span>
  );
}
